// The single frontend owner of custom-field vocabulary (#972).
// Python twins: utils/custom_field_keys.py::label_to_key and utils/custom_field_keys.py::label_key.
// fieldTypes.test.js keeps the two sides in step.

export const CUSTOM_FIELD_TYPES = [
    { value: 'text', label: 'Text' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'select', label: 'Dropdown' },
    { value: 'multi_select', label: 'Multi-Select' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'url', label: 'URL' },
];

// The nine types #972 allows when a field is created from the import wizard
// (no multi_select). The server enforces the same list for
// source = "list_management_import".
const IMPORT_CREATABLE = new Set([
    'text', 'textarea', 'number', 'date', 'select', 'email', 'phone', 'url', 'checkbox',
]);
export const IMPORT_CREATABLE_TYPES = CUSTOM_FIELD_TYPES.filter((t) => IMPORT_CREATABLE.has(t.value));

export const typeLabel = (value) =>
    (CUSTOM_FIELD_TYPES.find((t) => t.value === value) || {}).label || value;

// utils/custom_field_keys.py::label_to_key
export const labelToKey = (label) =>
    String(label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

// utils/custom_field_keys.py::label_key (str.isalnum is Unicode-aware, so are these).
// Lowercased one character at a time, like the Python: lowercasing the whole
// string would turn a word-final Σ into ς, which Python's per-character
// lower() never does.
export const labelKey = (label) =>
    Array.from(String(label || ''))
        .filter((ch) => /[\p{L}\p{N}]/u.test(ch))
        .map((ch) => ch.toLowerCase())
        .join('');

// Blank = null / undefined / '' / []. Zero and false are values.
export const isBlankCustomValue = (v) =>
    v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

// '' for an empty or non-numeric entry, otherwise Number(raw), 0 kept.
export const toNumberInput = (raw) => {
    if (raw === null || raw === undefined) return '';
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : '';
    const text = String(raw).trim();
    if (text === '') return '';
    const n = Number(text);
    return Number.isFinite(n) ? n : '';
};

// Checkbox values: imports before #972 wrote the raw cell text, so a Checkbox field can hold the
// strings "false", "0", "no" or "n" (any case, padded) as well as a real boolean. Those read as
// unticked everywhere (display, the edit checkbox, List Management, the Contacts / Companies
// tables); only a real `true` or any other non-blank value reads as ticked.
const FALSY_CHECKBOX_TEXT = new Set(['', 'false', '0', 'no', 'n']);
export const isTruthyCheckboxValue = (v) => {
    if (typeof v === 'string') return !FALSY_CHECKBOX_TEXT.has(v.trim().toLowerCase());
    return Boolean(v);
};
